import { DialogState, DriverStatus, SidebarContentId, SortByField } from '../../../types';
import { DriverStatusInRoute, type LocationChangePayload, type RouteState } from './types';
import * as qs from 'qs';
import { PARSE_OPTIONS, STRINGIFY_OPTIONS } from './qsUtil';
import { enumFromEnumValue } from './enumUtils';
import { driverIdSchema } from '../schema';
import type { RootState } from '../../../configuration/setup/store';
import SortDirection from '@rio-cloud/rio-uikit/SortDirection';

const DRIVERS_PATH = '/drivers';
const SORT_QUERY_PARAM = 'sort';
const DEFAULT_SORT_BY = SortByField.FIELD_LAST_NAME;
const DEFAULT_SORT_DIR = SortDirection.ASCENDING;
const DEFAULT_STATUS = DriverStatusInRoute.ACTIVE;
const DEFAULT_TAB = SidebarContentId.INFO;

const parseSortQuery = (
    sort: string | undefined,
    legacySortBy: string | undefined,
    legacySortDir: string | undefined
): Pick<RouteState, 'sortBy' | 'sortDir'> => {
    if (sort) {
        const decodedSort = decodeURIComponent(sort);
        const normalizedSort = decodedSort.startsWith(' ') ? `+${decodedSort.slice(1)}` : decodedSort;
        const sortDirection = normalizedSort.startsWith('-')
            ? SortDirection.DESCENDING
            : normalizedSort.startsWith('+')
              ? SortDirection.ASCENDING
              : undefined;
        const sortBy = sortDirection ? normalizedSort.substring(1) : undefined;

        return {
            sortBy,
            sortDir: sortDirection,
        };
    }

    return {
        sortBy: legacySortBy,
        sortDir: enumFromEnumValue(SortDirection, legacySortDir),
    };
};

const createSortQuery = (sortBy: string | undefined, sortDir: string | undefined) => {
    if (!sortBy || !sortDir) {
        return undefined;
    }

    return `${sortDir === SortDirection.ASCENDING ? '+' : '-'}${sortBy}`;
};

export const parseRoute = (payload: LocationChangePayload): RouteState => {
    const { search: locationSearch, pathname: locationPathname } = payload.location;
    const { q, search, sort, status, sortBy, sortDir, tab, createDriverModal } = qs.parse(
        locationSearch.replace('?', ''),
        PARSE_OPTIONS
    );
    const parsedSort = parseSortQuery(
        decodeUriComponentIfPresent(sort),
        decodeUriComponentIfPresent(sortBy),
        decodeUriComponentIfPresent(sortDir)
    );

    return {
        driverId: getDriverIdFragmentFromPath(locationPathname),
        search: decodeUriComponentIfPresent(q) ?? decodeUriComponentIfPresent(search),
        sortBy: parsedSort.sortBy,
        sortDir: parsedSort.sortDir,
        status: enumFromEnumValue(DriverStatusInRoute, decodeUriComponentIfPresent(status)),
        tab: enumFromEnumValue(SidebarContentId, decodeUriComponentIfPresent(tab)),
        createDriverModal: enumFromEnumValue(DialogState, decodeUriComponentIfPresent(createDriverModal)),
    };
};

const getDriverIdFragmentFromPath = (path: string) => {
    const decodedPath = decodeURIComponent(path);
    const pathWithoutBase = decodedPath.replace(`${DRIVERS_PATH}`, '');
    const driverId = pathWithoutBase.startsWith('/') ? pathWithoutBase.replace('/', '') : pathWithoutBase;
    if (!driverIdSchema.safeParse(driverId).success) {
        return undefined;
    }
    return driverId ? driverId : undefined;
};

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const decodeUriComponentIfPresent = (uriComponent: any): string | undefined => {
    return uriComponent && decodeURIComponent(uriComponent as string);
};

export const prepareState = (parsedUrlState: RouteState, state: RootState): RootState => {
    const newState: RootState = {
        ...state,
    };
    if (parsedUrlState.driverId !== undefined) {
        newState.app.selectedDriverId = parsedUrlState.driverId;
    }
    if (parsedUrlState.search !== undefined) {
        newState.app.searchText = parsedUrlState.search;
    }
    if (parsedUrlState.sortBy !== undefined) {
        newState.app.sortBy = parsedUrlState.sortBy;
    }
    if (parsedUrlState.sortDir !== undefined) {
        newState.app.sortDir = parsedUrlState.sortDir;
    }
    if (parsedUrlState.status !== undefined) {
        newState.app.driverStatuses = deduceDriverStatusesFrom(parsedUrlState.status);
    }
    if (parsedUrlState.tab !== undefined) {
        newState.sidebar.contentId = parsedUrlState.tab;
        newState.sidebar.pendingTabChangeNextTab = parsedUrlState.tab;
    }
    return newState;
};

export const getDriversUriForDeepLink = (state: RootState) => {
    const driverId = state.app.selectedDriverId;
    const driverFragment = driverId ? `/${driverId}` : '';
    const routeStatus = state.app.driverStatuses && deduceDriverStatusInRouteFrom(new Set(state.app.driverStatuses));
    const routeSort = createSortQuery(state.app.sortBy, state.app.sortDir);
    const queryParams = qs.stringify(
        {
            q: state.app.searchText || undefined,
            status: routeStatus === DEFAULT_STATUS ? undefined : routeStatus,
            [SORT_QUERY_PARAM]:
                state.app.sortBy === DEFAULT_SORT_BY && state.app.sortDir === DEFAULT_SORT_DIR ? undefined : routeSort,
            tab: state.sidebar?.contentId === DEFAULT_TAB ? undefined : state.sidebar?.contentId,
            createDriverModal: state.app.driverCreation?.showCreationDialog ? DialogState.SHOW : undefined,
        },
        STRINGIFY_OPTIONS
    );
    const normalizedQueryParams = queryParams?.replace(/sort=%2B/gi, 'sort=+').replace(/sort=%2D/gi, 'sort=-') ?? '';
    const searchFragment = normalizedQueryParams && `?${normalizedQueryParams}`;
    return encodeURI(`${DRIVERS_PATH}${driverFragment}`) + searchFragment;
};

export const deduceDriverStatusesFrom = (driverStatusInRoute: DriverStatusInRoute): DriverStatus[] => {
    switch (driverStatusInRoute) {
        case DriverStatusInRoute.ACTIVE:
            return [DriverStatus.ACTIVE];
        case DriverStatusInRoute.ARCHIVED:
            return [DriverStatus.ARCHIVED];
        case DriverStatusInRoute.ALL:
            return [DriverStatus.ACTIVE, DriverStatus.ARCHIVED];
    }
};

const deduceDriverStatusInRouteFrom = (driverStatuses: Set<DriverStatus>): DriverStatusInRoute | undefined => {
    let urlParamDriverStatus: undefined | DriverStatusInRoute;
    switch (driverStatuses.size) {
        case 0:
            urlParamDriverStatus = undefined;
            break;
        case 1:
            urlParamDriverStatus = driverStatuses.values().next().value as unknown as DriverStatusInRoute;
            break;
        case 2:
            urlParamDriverStatus = DriverStatusInRoute.ALL;
            break;
        default:
            throw new Error(`Invalid number of checked driverStatuses. ${driverStatuses}`);
    }
    return urlParamDriverStatus;
};
